import { ButtonType, ButtonWidget, PropsAndInputAttrs } from '@tableau/widgets';
import * as React from 'react';

import { Messages } from '../../codegen/Localize';
import { FileDragAndDrop, FileDragAndDropProps, preventDefaultDrag } from '../../uploadContent/components/FileDragAndDrop';
import { BulkImportUserDialogStyle } from './styles/BulkImportUserDialogStyle';

export type FileInputProps = Omit<Partial<FileDragAndDropProps>, 'handleDrop'> & {
    /**
     * If true, a droppable area will be rendered to enable file drag and drop,
     * and if `children` is provided, it will be rendered in place of the default content inside the droppable area.
     * 
     * Note: `children` is completely ignored when `allowFileDrop` is falsy.
     */
    allowFileDrop?: boolean;

    /** Callback with the selected file (undefined if no file was selected) and the action that triggered file selection. */
    handleFileInput: (file: File | undefined, source: 'DragDrop' | 'SelectButton') => void;

    /**
     * The name of the selected file as a controlled state (i.e. managed by the parent component).
     * Typically used to validate a file before displaying it as selected.
     */
    selectedFileName?: string;
};

/**
 * Accessible component for selecting a file from the user's device storage. 
 * 
 * By default, a visually hidden `role='status'` element (important for assistive technologies) and a visually hidden `<input>` element will be rendered.
 * The `<input>` element can be accessed via a ref, and then calling the `click` method typically opens the OS default file browser.
 * https://developer.mozilla.org/en-US/docs/Web/API/File_API/Using_files_from_web_applications#using_hidden_file_input_elements_using_the_click_method
 */
export const FileInput = React.forwardRef<HTMLInputElement, PropsAndInputAttrs<FileInputProps>>((props, forwardRef) => {
    const { activeStateClassName, allowFileDrop, children, containerClassName, defaultStateClassName, handleFileInput, onDragClassName, selectedFileName: controlledSelectedFileName, testId, ...htmlAttrs } = props;
    const [selectedFileName, setSelectedFileName] = React.useState<string>(undefined);
    const fileName = controlledSelectedFileName ?? selectedFileName;
    const inputRef = React.useRef<HTMLInputElement>(null);

    const handleFileSelection = React.useCallback((event: React.ChangeEvent<HTMLInputElement>, source: 'DragDrop' | 'SelectButton') => {
        if (event.target.files.length) {
            const fileInput: File = event.target.files[0];
            const name = fileInput.name.split('\\').pop();
            setSelectedFileName(name);
            handleFileInput(fileInput, source);
        } else {
            setSelectedFileName(undefined);
            handleFileInput(undefined, source);
        }
    }, [handleFileInput]);

    const assignRefs = React.useCallback((ref: HTMLInputElement) => {
        inputRef.current = ref;
        if (forwardRef) {
            typeof forwardRef === 'function'
                ? forwardRef(ref)
                : (forwardRef as React.MutableRefObject<HTMLInputElement>).current = ref;
        }
    }, [forwardRef]);

    return <>
        <div aria-live='polite' className='sr-only' role='status'>{fileName ? Messages.fileSelected({ FILE_NAME: fileName }) : ''}</div>
        <input
            data-tb-test-id={testId}
            {...htmlAttrs}
            onChange={e => handleFileSelection(e, 'SelectButton')}
            ref={assignRefs}
            style={{ display: 'none' }}
            type='file'
        />
        {allowFileDrop && <FileDragAndDrop
            activeStateClassName={activeStateClassName ?? BulkImportUserDialogStyle.activeOutline}
            containerClassName={containerClassName ?? BulkImportUserDialogStyle.dragAndDropContainer}
            defaultStateClassName={defaultStateClassName ?? BulkImportUserDialogStyle.defaultOutline}
            handleDrop={e => handleFileSelection(e, 'DragDrop')}
            onDragClassName={onDragClassName ?? BulkImportUserDialogStyle.focusOutline}
            testId={testId}
        >
            { children ??
                <div className={BulkImportUserDialogStyle.dragAndDropChildren} onDragLeave={preventDefaultDrag}>
                    { fileName
                        ? <b className={BulkImportUserDialogStyle.fileName}>{fileName}</b>
                        : <>
                            <p className={BulkImportUserDialogStyle.dragAndDropText}>{Messages.contentUpload_dragAndDrop()}</p>
                            <p className={BulkImportUserDialogStyle.dragAndDropOrText}>{Messages.contentUpload_dragAndDropOr()}</p>
                        </>
                    }
                    <ButtonWidget
                        buttonType={ButtonType.Outline}
                        children={Messages.contentUpload_chooseAFile()}
                        handleClick={() => inputRef.current.click()}
                        testId={testId}
                        type='button' // this is needed to prevent form submission when clicking this button
                    />
                </div>
            }
        </FileDragAndDrop>}
    </>;
});
FileInput.displayName = 'FileInput';
